import { EChartsOption } from "echarts-for-react";
import { Device } from "./Gateways";
import { Alert as _Alert, AlertNotice as _AlertNotice } from "generated/models";
import { ChartSource, ChartType } from "constant/chart";

type AlertNotice = Omit<_AlertNotice, "create_time" | "modify_time"> & {
    create_time: string;
    modify_time?: string;
};

export type Alert = Omit<_Alert, "create_time" | "modify_time" | "notice"> & {
    create_time: string;
    modify_time?: string;
    notice?: AlertNotice;
};

export type BlockKeyProps = {
    key: string;
};

export type MultipleChartColumnData = {
    data: {
        [key: number]: {
            devices: {
                [key: string]: any;
            };
            sensor_type: string;
        };
    };
    chart_name: string;
    multipleChartType: "same" | "different";
};

export type MultipleChartBlockProps = {
    isEdit?: boolean;
    blockKey: BlockKeyProps;
    defaultValue?: MultipleChartColumnData;
    isSingleUpdate?: boolean;
    onDelete?: (item: BlockKeyProps) => void;
    onChange: (data: any) => void;
    onSubmitMultipleChart?: Function;
};

export type MultipleChartColumnProps = {
    isEdit?: boolean;
    data: MultipleChartColumnData;
    defaultValue?: MultipleChartColumnData;
    column: number;
    onChange: (data: any) => void;
};

export type ChartState = {
    option: EChartsOption;
    hasFetchedConfig: boolean;
    sensorConfigRes: any;
};

export type RefreshRate = {
    seconds: number | "auto";
    label?: string;
};

export type BarChartSeries = {
    type: string;
    name: string;
    data: any[];
    barWidth?: number | string;
    color: string;
    stack?: string;
    id?: string;
};

export type CustomEChartParams = {
    liveData: any;
    chartState?: ChartState;
    liveTime: any;
    liveDate: any;
    sensorUnit: string;
    min?: any;
    max?: any;
    accuracy?: string | number;
    sensorType?: string;
    lastTimeRangeSelect?: any;
    chart: string;
};

export type GatewayWidgetAttribute = {
    gateway_id: string;
    show_location?: boolean;
};

export type ChartPanel = {
    attributes: Array<{
        gateway_id: string;
        color?: string;
        unit?: string;
        device_id?: string;
        said?: number;
        show_alert?: boolean;
        show_location?: boolean;
        name: string;
    }>;
    name: string;
    source: ChartSource;
    // TODO: Refine to only use one type
    chart: ChartType;
    uuid: string;
    queries?: string[];
    refresh_rate?: string | null;
    time_range?: string | null;
    query_time?: number | null;
};

export type BaseWidgetProps = {
    panel: any;
    zoomLevel: number;
    dashboardId: string;
    isVisible?: boolean;
    visibleRef?: any;
    allRegistry?: any; // Add type
};

export type BaseWidgetWithDeviceProps = BaseWidgetProps & {
    deviceConfig?: DeviceInfo[];
};

export type WidgetProps = BaseWidgetProps & {
    gatewaysConfig?: any;
    onDelete: (UID: string) => void;
    onSizeChange?: (uuid: string, width: number) => void;
    allAlerts?: Alert[];
};

export type BasicSensorChartProps = WidgetProps & {
    customEChartStyle: (chartColor: string) => any[];
    customEChartData?: (params: CustomEChartParams) => void;
    displayName: (chartState: ChartState) => string;
    isFourWiredMotor?: boolean;
    isActuator?: boolean;
};

export type CardProps = Omit<
    BasicSensorChartProps,
    "customEChartStyle" | "customEChartData" | "displayName"
>;

export enum ConsumptionChartBy {
    Day = "Day",
    Week = "Week",
    Month = "Month",
    Year = "Year",
}

export type ConsumptionChartData = {
    series: BarChartSeries[];

    categories: string[] | number[];

    /** Sum of all series values for the current period. {@link null} indicates no data. */
    total: number | null;

    /** Sum of all series values for the previous period. {@link null} indicates no data. */
    previousTotal: number | null;
};

export type HeatmapTarget = {
    deviceId: string;
    targetId: string;
    x: number;
    y: number;
    z: number;
    intensity: number;
};

export type DeviceInfo = Device & {
    readings: {
        lowest: number;
        highest: number;
        value: number;
        lastActive: number;
    };
    chartColor: string;
    metric: string;
};

export type DeviceSelected = Device & {
    unitOptions?: string[];
    chartName?: string;
    color?: string | [number, string][];
    alert?: boolean;
};

export type ColorStop = [number, string];

export type PSLStatus = {
    gatewayId: string;
    deviceId: string;
    status: number;
};
